// src/components/RichTextEditor.jsx
import React, { useRef, useCallback, useEffect, useState } from 'react';
import './RichTextEditor.css';
import { 
  Bold, 
  Italic, 
  Underline, 
  AlignLeft, 
  AlignCenter, 
  AlignRight, 
  AlignJustify,
  List,
  ListOrdered,
  Link,
  Image,
  Type,
  Heading1,
  Heading2,
  Heading3,
  RotateCcw,
  Minus,
  Trash2
} from 'lucide-react';

const RichTextEditor = ({ value, onChange, placeholder = "Enter your content here..." }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedImg, setSelectedImg] = useState(null);
  const [imgWidth, setImgWidth] = useState(100);
  const [imgFloat, setImgFloat] = useState('center');
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0, visible: false });

  // Handle editor click for selecting images
  useEffect(() => {
    const handleEditorClick = (e) => {
      const target = e.target;
      if (target.tagName === 'IMG') {
        setSelectedImg(target);
        
        let currentWidth = 100;
        if (target.style.width) {
          if (target.style.width.endsWith('%')) {
            currentWidth = parseInt(target.style.width, 10);
          } else if (target.style.width.endsWith('px')) {
            const editorWidth = editorRef.current?.offsetWidth || 1;
            currentWidth = Math.round((target.offsetWidth / editorWidth) * 100);
          }
        }
        setImgWidth(currentWidth);

        let currentFloat = 'center';
        if (target.style.float === 'left') {
          currentFloat = 'left';
        } else if (target.style.float === 'right') {
          currentFloat = 'right';
        }
        setImgFloat(currentFloat);

        editorRef.current?.querySelectorAll('img').forEach(img => {
          img.classList.remove('selected-img');
        });
        target.classList.add('selected-img');
      } else {
        editorRef.current?.querySelectorAll('img').forEach(img => {
          img.classList.remove('selected-img');
        });
        setSelectedImg(null);
      }
    };

    const handleWindowClick = (e) => {
      if (editorRef.current && !editorRef.current.contains(e.target) && !e.target.closest('.image-edit-toolbar')) {
        editorRef.current.querySelectorAll('img').forEach(img => {
          img.classList.remove('selected-img');
        });
        setSelectedImg(null);
      }
    };

    const editor = editorRef.current;
    editor?.addEventListener('click', handleEditorClick);
    window.addEventListener('mousedown', handleWindowClick);
    
    return () => {
      editor?.removeEventListener('click', handleEditorClick);
      window.removeEventListener('mousedown', handleWindowClick);
    };
  }, []);

  // Update floating toolbar position
  useEffect(() => {
    if (selectedImg && editorRef.current) {
      const editorRect = editorRef.current.getBoundingClientRect();
      const imgRect = selectedImg.getBoundingClientRect();
      
      const top = imgRect.top - editorRect.top + editorRef.current.scrollTop - 130; // position relative to container
      const left = imgRect.left - editorRect.left + editorRef.current.scrollLeft + (imgRect.width / 2) - 130;
      
      setToolbarPosition({
        top: Math.max(10, top),
        left: Math.max(10, Math.min(editorRect.width - 270, left)),
        visible: true
      });
    } else {
      setToolbarPosition({ top: 0, left: 0, visible: false });
    }
  }, [selectedImg, imgWidth, imgFloat]);

  const applyImageStyle = (width, alignment) => {
    if (!selectedImg) return;
    
    selectedImg.style.width = `${width}%`;
    
    if (alignment === 'left') {
      selectedImg.style.float = 'left';
      selectedImg.style.margin = '0.5rem 1.5rem 0.5rem 0';
      selectedImg.style.display = 'inline-block';
      selectedImg.style.clear = 'none';
    } else if (alignment === 'right') {
      selectedImg.style.float = 'right';
      selectedImg.style.margin = '0.5rem 0 0.5rem 1.5rem';
      selectedImg.style.display = 'inline-block';
      selectedImg.style.clear = 'none';
    } else {
      selectedImg.style.float = 'none';
      selectedImg.style.margin = '1rem auto';
      selectedImg.style.display = 'block';
      selectedImg.style.clear = 'both';
    }
    
    setImgWidth(width);
    setImgFloat(alignment);
    handleInput();
  };

  const deleteSelectedImage = () => {
    if (selectedImg) {
      selectedImg.remove();
      setSelectedImg(null);
      handleInput();
    }
  };

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  // Handle content changes
  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Execute formatting commands with better HTML output
  const execCommand = useCallback((command, value = null) => {
    // For font size, use CSS style instead of font tags
    if (command === 'fontSize') {
      const selection = window.getSelection();
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        const span = document.createElement('span');
        
        // Map font sizes to CSS
        const fontSizeMap = {
          '1': '10px',
          '2': '13px', 
          '3': '16px',
          '4': '18px',
          '5': '24px',
          '6': '32px',
          '7': '48px'
        };
        
        span.style.fontSize = fontSizeMap[value] || '16px';
        
        try {
          range.surroundContents(span);
          selection.removeAllRanges();
          selection.addRange(range);
        } catch (e) {
          // Fallback to document.execCommand if range is complex
          document.execCommand(command, false, value);
        }
      }
    } else {
      document.execCommand(command, false, value);
    }
    
    editorRef.current?.focus();
    handleInput();
  }, [handleInput]);

  // Format buttons configuration
  const formatButtons = [
    { command: 'bold', icon: Bold, tooltip: 'Bold' },
    { command: 'italic', icon: Italic, tooltip: 'Italic' },
    { command: 'underline', icon: Underline, tooltip: 'Underline' },
  ];

  const alignButtons = [
    { command: 'justifyLeft', icon: AlignLeft, tooltip: 'Align Left' },
    { command: 'justifyCenter', icon: AlignCenter, tooltip: 'Align Center' },
    { command: 'justifyRight', icon: AlignRight, tooltip: 'Align Right' },
    { command: 'justifyFull', icon: AlignJustify, tooltip: 'Justify' },
  ];

  const listButtons = [
    { command: 'insertUnorderedList', icon: List, tooltip: 'Bullet List' },
    { command: 'insertOrderedList', icon: ListOrdered, tooltip: 'Numbered List' },
  ];

  const headingButtons = [
    { command: 'formatBlock', value: 'h1', icon: Heading1, tooltip: 'Heading 1' },
    { command: 'formatBlock', value: 'h2', icon: Heading2, tooltip: 'Heading 2' },
    { command: 'formatBlock', value: 'h3', icon: Heading3, tooltip: 'Heading 3' },
    { command: 'formatBlock', value: 'p', icon: Type, tooltip: 'Paragraph' },
  ];

  // Handle font size change
  const handleFontSize = (size) => {
    execCommand('fontSize', size);
  };

  // Handle text color change
  const handleTextColor = (color) => {
    execCommand('foreColor', color);
  };

  // Handle link insertion
  const insertLink = () => {
    const url = prompt('Enter URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  // Handle clear formatting
  const clearFormatting = () => {
    execCommand('removeFormat');
    execCommand('formatBlock', 'p');
  };

  // Insert horizontal rule
  const insertHorizontalRule = () => {
    execCommand('insertHorizontalRule');
  };

  // Handle image insertion
  const insertImage = () => {
    fileInputRef.current?.click();
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        execCommand('insertImage', event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Button component
  const ToolbarButton = ({ onClick, icon: Icon, tooltip, isActive = false }) => (
    <button
      type="button"
      onClick={onClick}
      className={`p-2 rounded-md transition-colors ${
        isActive 
          ? 'bg-cyan-500 text-black' 
          : 'bg-gray-600 text-white hover:bg-gray-500'
      }`}
      title={tooltip}
    >
      <Icon size={16} />
    </button>
  );

  return (
    <div className="w-full border border-gray-600 rounded-lg overflow-hidden bg-gray-700 rich-text-editor relative">
      {/* Toolbar */}
      <div className="bg-gray-800 p-3 border-b border-gray-600">
        <div className="flex flex-wrap gap-2">
          {/* Font Size */}
          <select
            onChange={(e) => handleFontSize(e.target.value)}
            className="px-2 py-1 bg-gray-600 text-white rounded text-sm border border-gray-500"
            defaultValue="3"
          >
            <option value="1">Very Small</option>
            <option value="2">Small</option>
            <option value="3">Normal</option>
            <option value="4">Medium</option>
            <option value="5">Large</option>
            <option value="6">Very Large</option>
            <option value="7">Huge</option>
          </select>

          {/* Text Color */}
          <div className="relative">
            <select
              onChange={(e) => handleTextColor(e.target.value)}
              className="px-2 py-1 bg-gray-600 text-white rounded text-sm border border-gray-500"
              defaultValue="#000000"
            >
              <option value="#000000">Black</option>
              <option value="#ef4444">Red</option>
              <option value="#22c55e">Green</option>
              <option value="#3b82f6">Blue</option>
              <option value="#06b6d4">Cyan</option>
              <option value="#8b5cf6">Purple</option>
              <option value="#f59e0b">Orange</option>
              <option value="#6b7280">Gray</option>
            </select>
          </div>

          <div className="w-px bg-gray-600 mx-1"></div>

          {/* Headings */}
          <div className="flex gap-1">
            {headingButtons.map((btn) => (
              <ToolbarButton
                key={btn.command + (btn.value || '')}
                onClick={() => execCommand(btn.command, btn.value)}
                icon={btn.icon}
                tooltip={btn.tooltip}
              />
            ))}
          </div>

          <div className="w-px bg-gray-600 mx-1"></div>

          {/* Text Formatting */}
          <div className="flex gap-1">
            {formatButtons.map((btn) => (
              <ToolbarButton
                key={btn.command}
                onClick={() => execCommand(btn.command)}
                icon={btn.icon}
                tooltip={btn.tooltip}
              />
            ))}
          </div>

          <div className="w-px bg-gray-600 mx-1"></div>

          {/* Alignment */}
          <div className="flex gap-1">
            {alignButtons.map((btn) => (
              <ToolbarButton
                key={btn.command}
                onClick={() => execCommand(btn.command)}
                icon={btn.icon}
                tooltip={btn.tooltip}
              />
            ))}
          </div>

          <div className="w-px bg-gray-600 mx-1"></div>

          {/* Lists */}
          <div className="flex gap-1">
            {listButtons.map((btn) => (
              <ToolbarButton
                key={btn.command}
                onClick={() => execCommand(btn.command)}
                icon={btn.icon}
                tooltip={btn.tooltip}
              />
            ))}
          </div>

          <div className="w-px bg-gray-600 mx-1"></div>

          {/* Link and Image */}
          <div className="flex gap-1">
            <ToolbarButton
              onClick={insertLink}
              icon={Link}
              tooltip="Insert Link"
            />
            <ToolbarButton
              onClick={insertImage}
              icon={Image}
              tooltip="Insert Image"
            />
            <ToolbarButton
              onClick={insertHorizontalRule}
              icon={Minus}
              tooltip="Insert Horizontal Rule"
            />
          </div>

          <div className="w-px bg-gray-600 mx-1"></div>

          {/* Clear Formatting */}
          <div className="flex gap-1">
            <ToolbarButton
              onClick={clearFormatting}
              icon={RotateCcw}
              tooltip="Clear Formatting"
            />
          </div>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="p-4 min-h-[300px] bg-white text-black focus:outline-none prose max-w-none
                   empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400 empty:before:pointer-events-none"
        style={{ 
          lineHeight: '1.6',
          fontSize: '16px'
        }}
        data-placeholder={placeholder}
        suppressContentEditableWarning={true}
      />

      {/* Hidden file input for image uploads */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />

      {/* Floating Image Editor Toolbar */}
      {toolbarPosition.visible && (
        <div 
          className="image-edit-toolbar bg-slate-900/95 text-white rounded-lg p-3 shadow-xl border border-slate-700 backdrop-blur-md flex flex-col gap-2.5 z-[99]"
          style={{
            position: 'absolute',
            top: `${toolbarPosition.top}px`,
            left: `${toolbarPosition.left}px`,
            width: '260px',
          }}
        >
          <div className="flex justify-between items-center border-b border-slate-800 pb-1.5">
            <span className="text-xs font-bold text-cyan-400 uppercase tracking-wider">Image Settings</span>
            <button 
              type="button" 
              onClick={() => setSelectedImg(null)}
              className="text-gray-400 hover:text-white text-xs font-semibold"
            >
              Close
            </button>
          </div>
          
          {/* Float Alignment */}
          <div className="flex flex-col gap-1">
            <label className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider">Alignment / Wrap</label>
            <div className="grid grid-cols-3 gap-1 bg-slate-950 p-0.5 rounded-md">
              <button
                type="button"
                onClick={() => applyImageStyle(imgWidth, 'left')}
                className={`py-1 text-xs rounded transition-all font-medium ${
                  imgFloat === 'left' ? 'bg-cyan-500 text-black shadow-md font-bold' : 'text-gray-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                Float Left
              </button>
              <button
                type="button"
                onClick={() => applyImageStyle(imgWidth, 'center')}
                className={`py-1 text-xs rounded transition-all font-medium ${
                  imgFloat === 'center' ? 'bg-cyan-500 text-black shadow-md font-bold' : 'text-gray-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                Center
              </button>
              <button
                type="button"
                onClick={() => applyImageStyle(imgWidth, 'right')}
                className={`py-1 text-xs rounded transition-all font-medium ${
                  imgFloat === 'right' ? 'bg-cyan-500 text-black shadow-md font-bold' : 'text-gray-400 hover:text-white hover:bg-slate-800'
                }`}
              >
                Float Right
              </button>
            </div>
          </div>

          {/* Width Size Slider */}
          <div className="flex flex-col gap-1">
            <div className="flex justify-between items-center text-[10px] text-gray-400 font-semibold uppercase tracking-wider">
              <span>Width</span>
              <span className="text-cyan-400 font-mono font-bold">{imgWidth}%</span>
            </div>
            <input
              type="range"
              min="10"
              max="100"
              step="5"
              value={imgWidth}
              onChange={(e) => applyImageStyle(parseInt(e.target.value, 10), imgFloat)}
              className="w-full accent-cyan-500 bg-slate-950 rounded-lg appearance-none h-1 cursor-pointer"
            />
            {/* Quick Presets */}
            <div className="grid grid-cols-4 gap-1 mt-1 text-[9px] font-mono">
              {[25, 50, 75, 100].map((pct) => (
                <button
                  key={pct}
                  type="button"
                  onClick={() => applyImageStyle(pct, imgFloat)}
                  className={`py-0.5 rounded transition-all text-center border ${
                    imgWidth === pct 
                      ? 'border-cyan-500 text-cyan-400 bg-cyan-950/20 font-bold' 
                      : 'border-slate-800 text-gray-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  {pct}%
                </button>
              ))}
            </div>
          </div>

          {/* Actions - Delete */}
          <div className="border-t border-slate-800 pt-2 flex justify-end">
            <button
              type="button"
              onClick={deleteSelectedImage}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-red-600/20 text-red-400 hover:bg-red-600 hover:text-white border border-red-500/30 rounded-md transition-all font-medium"
            >
              <Trash2 size={12} />
              Delete Image
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
